/**
 * remark plugin that highlights `[[topic.field]]` skill-body references in
 * rendered markdown, mirroring the chips the skill editor shows while editing.
 *
 * `[[name]]` is not markdown syntax, so (absent a matching link-reference
 * definition, which skill bodies don't write) the whole token survives parsing
 * as literal text. The plugin scans text nodes for the same grammar the editor
 * uses ({@link segmentTopicReferences} — the single source of segmentation for
 * both surfaces) and splits each hit into styled spans: a topic-name chip
 * (known vs not-yet-known) and an optional field-path chip. Unlike the editor
 * overlay, the `[[` / `]]` delimiters are dropped here — this is rendered view,
 * not editing, so (like markdown hiding its own syntax) only the highlighted
 * name shows.
 *
 * Spans are emitted as `emphasis` nodes overridden to `<span>` via
 * `data.hName` / `data.hProperties` — the standard mdast→hast escape hatch,
 * chosen over a custom node type so every mdast consumer treats the node as a
 * known inline. Code blocks (fenced or inline) and links are skipped, so a
 * `[[name]]` literal inside a code sample passes through unchanged.
 */

import { segmentTopicReferences } from "@/shared/domain/skills/topicReference";

import { TOPIC_SEGMENT_CLASS } from "../topicReferenceTokens";

interface MdastNode {
  type: string;
  value?: string;
  children?: MdastNode[];
  data?: unknown;
}

function styledSpan(text: string, className: string): MdastNode {
  return {
    type: "emphasis",
    data: { hName: "span", hProperties: { className } },
    children: [{ type: "text", value: text }],
  };
}

function walk(node: MdastNode, knownTopicNames: readonly string[]): void {
  if (
    node.type === "code" ||
    node.type === "inlineCode" ||
    node.type === "link"
  ) {
    return;
  }
  if (!node.children) {
    return;
  }

  const next: MdastNode[] = [];
  let changed = false;
  for (const child of node.children) {
    if (
      child.type === "text" &&
      child.value !== undefined &&
      child.value.includes("[[")
    ) {
      const segments = segmentTopicReferences(child.value, knownTopicNames);
      if (segments.some((segment) => segment.kind !== "plain")) {
        changed = true;
        for (const segment of segments) {
          // The `[[` / `]]` delimiters are authoring markers, not shown in the
          // rendered view — only the highlighted name and field path remain.
          if (segment.kind === "bracket") {
            continue;
          }
          const className = TOPIC_SEGMENT_CLASS[segment.kind];
          next.push(
            className === undefined
              ? { type: "text", value: segment.text }
              : styledSpan(segment.text, className),
          );
        }
        continue;
      }
    }
    walk(child, knownTopicNames);
    next.push(child);
  }
  if (changed) {
    node.children = next;
  }
}

export function remarkTopicReferences(options: {
  knownTopicNames: readonly string[];
}) {
  return (tree: MdastNode, _file?: unknown) => {
    walk(tree, options.knownTopicNames);
  };
}
